import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Mail, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api, enc, errMsg } from "@/lib/api";
import { loadTools } from "@/lib/tools";

export const rowInfo = (r) => ({
  company: r.company || r.company_name || r.job?.company || r.name || "the team",
  title: r.title || r.job_title || r.role || r.job?.title || "",
  jobId: r.job_id || r.job?.id || r.id || "",
  days: r.days_silent ?? r.days_since ?? r.silent_days ?? r.days ?? r.days_since_applied ?? null,
  recruiterName: r.recruiter_name || "",
  recruiterEmail: r.recruiter_email || "",
});

const draft = (i, profile, user) => {
  const role = i.title ? `the ${i.title} role` : "the open role";
  const when = i.days != null ? ` about ${i.days} days ago` : " recently";
  const skills = (profile?.skills || []).slice(0, 3).join(", ");
  return {
    subject: `Following up on my application${i.title ? ` for ${i.title}` : ""}`,
    body: `Hi ${i.recruiterName || `${i.company} Hiring Team`},\n\nI hope you're doing well. I applied for ${role} at ${i.company}${when} and wanted to follow up, as I'm still very interested in the opportunity.\n\n${profile?.headline ? `As a ${profile.headline}` : "With my background"}${skills ? ` with strengths in ${skills}` : ""}, I'm confident I could contribute quickly to your team.\n\nWould you be able to share an update on the timeline or next steps? I'm happy to provide anything else you need.\n\nThank you for your time,\n${user?.name || ""}${profile?.linkedin_url ? `\n${profile.linkedin_url}` : ""}`,
  };
};

const sectionsToText = (d) => (d?.sections || []).map((s) => (typeof s === "string" ? s : [s.heading || s.title, s.body || s.content || s.text, ...(s.items || [])].filter(Boolean).join("\n"))).join("\n\n");

export const FollowUpDialog = ({ row, onClose }) => {
  const { profile, user } = useAuth();
  const info = rowInfo(row);
  const [mail, setMail] = useState(() => draft(info, profile, user));
  const [to, setTo] = useState(info.recruiterEmail);
  const [aiTool, setAiTool] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    loadTools().then((d) => setAiTool((d?.tools || []).find((t) => /follow.?up/i.test(`${t.id} ${t.title}`)) || null));
    if (!info.recruiterEmail && info.jobId) api.get(`/jobs/${enc(info.jobId)}`).then((r) => r.data?.application?.recruiter_email && setTo(r.data.application.recruiter_email)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const polish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ai/tools/${aiTool.id}`, { job_id: info.jobId || null, inputs: { company: info.company, days_silent: info.days, draft: mail.body } });
      const text = data.email || data.body || sectionsToText(data);
      if (text) setMail((m) => ({ subject: data.subject || m.subject, body: text }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard.writeText(`Subject: ${mail.subject}\n\n${mail.body}`).then(() => toast.success("Email copied"), () => toast.error("Couldn't copy. Select the text and copy it manually."));
  const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(mail.subject)}&body=${encodeURIComponent(mail.body)}`;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] overflow-y-auto border-[#27272A] bg-[#18181B] sm:max-w-xl" data-testid="followup-dialog">
        <DialogHeader><DialogTitle className="font-display">Follow up with {info.company}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Recruiter email (optional)" className="bg-[#09090B]" data-testid="followup-to" />
          <Input value={mail.subject} onChange={(e) => setMail({ ...mail, subject: e.target.value })} className="bg-[#09090B]" data-testid="followup-subject" />
          <Textarea rows={12} value={mail.body} onChange={(e) => setMail({ ...mail, body: e.target.value })} className="bg-[#09090B] text-sm leading-relaxed" data-testid="followup-body" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild><a href={mailto} data-testid="followup-open-email"><Mail className="h-4 w-4" />Open in email</a></Button>
          <Button variant="outline" onClick={copy} data-testid="followup-copy"><Copy className="h-4 w-4" />Copy</Button>
          {aiTool && <Button variant="ghost" onClick={polish} disabled={busy} data-testid="followup-polish">{busy ? <Spinner /> : <Sparkles className="h-4 w-4 text-[#D4AF37]" />}Polish with AI</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
